import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  type DashboardRoleKey,
  buildStorageKey,
  getPresetPreferences,
  mergePreferencesWithCatalog,
  resolveCompanyId,
} from '../config/dashboardWidgetCatalog';
import {
  deleteDashboardPreferences,
  getDashboardPreferences,
  upsertDashboardPreferences,
} from '../services/dashboardPreferencesApi';

export interface WidgetConfig {
  id: string;
  visible: boolean;
  order: number;
}

export interface WidgetPreferences {
  widgets: WidgetConfig[];
  layout: 'grid' | 'list';
}

export const useWidgetPreferences = (dashboardRoleKey: DashboardRoleKey) => {
  const { user } = useAuth();
  const companyId = resolveCompanyId(user?.company_id);
  const userId = user?.id;

  const storageKey = useMemo(() => {
    if (!companyId || !userId) return null;
    return buildStorageKey(dashboardRoleKey, companyId, userId);
  }, [dashboardRoleKey, companyId, userId]);

  const [preferences, setPreferences] = useState<WidgetPreferences>(() =>
    getPresetPreferences(dashboardRoleKey)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  const persistLocal = useCallback(
    (prefs: WidgetPreferences) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(prefs));
      } catch (error) {
        console.error('Erro ao salvar preferências de widgets localmente:', error);
      }
    },
    [storageKey]
  );

  const loadFromServer = useCallback(async () => {
    if (!companyId || !userId) {
      setPreferences(getPresetPreferences(dashboardRoleKey));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setSyncError(null);

    try {
      const { data, useDefault } = await getDashboardPreferences(companyId, dashboardRoleKey);
      let merged: WidgetPreferences;
      if (useDefault || !data) {
        const cached = storageKey ? localStorage.getItem(storageKey) : null;
        if (cached) {
          merged = mergePreferencesWithCatalog(
            dashboardRoleKey,
            JSON.parse(cached) as WidgetPreferences
          );
        } else {
          merged = getPresetPreferences(dashboardRoleKey);
        }
      } else {
        merged = mergePreferencesWithCatalog(dashboardRoleKey, {
          layout: data.layout_mode,
          widgets: data.widgets,
        });
      }
      setPreferences(merged);
      persistLocal(merged);
      setIsDirty(false);
    } catch (error) {
      console.warn('Falha ao carregar preferências do servidor, usando cache local:', error);
      setSyncError('sync_failed');
      try {
        if (storageKey) {
          const cached = localStorage.getItem(storageKey);
          if (cached) {
            setPreferences(
              mergePreferencesWithCatalog(dashboardRoleKey, JSON.parse(cached) as WidgetPreferences)
            );
          } else {
            setPreferences(getPresetPreferences(dashboardRoleKey));
          }
        }
      } catch {
        setPreferences(getPresetPreferences(dashboardRoleKey));
      }
    } finally {
      setIsLoading(false);
    }
  }, [companyId, userId, dashboardRoleKey, storageKey, persistLocal]);

  useEffect(() => {
    loadFromServer();
  }, [loadFromServer]);

  const saveToServer = useCallback(
    async (prefs?: WidgetPreferences) => {
      if (!companyId || !userId) return;
      const payload = prefs ?? preferencesRef.current;
      try {
        await upsertDashboardPreferences(companyId, dashboardRoleKey, payload);
        persistLocal(payload);
        setIsDirty(false);
        setSyncError(null);
      } catch (error) {
        console.error('Erro ao salvar preferências no servidor:', error);
        setSyncError('save_failed');
        persistLocal(payload);
        throw error;
      }
    },
    [companyId, userId, dashboardRoleKey, persistLocal]
  );

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveToServer().catch(() => undefined);
    }, 500);
  }, [saveToServer]);

  const updatePreferences = useCallback(
    (updater: (prev: WidgetPreferences) => WidgetPreferences, options?: { save?: boolean }) => {
      setPreferences(prev => {
        const next = updater(prev);
        persistLocal(next);
        return next;
      });
      setIsDirty(true);
      if (options?.save !== false) {
        scheduleSave();
      }
    },
    [persistLocal, scheduleSave]
  );

  const updateWidgetVisibility = useCallback(
    (widgetId: string, visible: boolean) => {
      updatePreferences(prev => ({
        ...prev,
        widgets: prev.widgets.map(w => (w.id === widgetId ? { ...w, visible } : w)),
      }));
    },
    [updatePreferences]
  );

  const updateWidgetOrder = useCallback(
    (widgetIds: string[]) => {
      updatePreferences(prev => ({
        ...prev,
        widgets: widgetIds.map((id, index) => {
          const widget = prev.widgets.find(w => w.id === id);
          return widget ? { ...widget, order: index } : { id, visible: true, order: index };
        }),
      }));
    },
    [updatePreferences]
  );

  const resetToDefault = useCallback(async () => {
    const preset = getPresetPreferences(dashboardRoleKey);
    setPreferences(preset);
    persistLocal(preset);
    setIsDirty(false);
    if (companyId && userId) {
      try {
        await deleteDashboardPreferences(companyId, dashboardRoleKey);
        setSyncError(null);
      } catch (error) {
        console.error('Erro ao resetar preferências no servidor:', error);
        setSyncError('reset_failed');
      }
    }
  }, [dashboardRoleKey, companyId, userId, persistLocal]);

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (isDirty) {
      await saveToServer();
    }
  }, [isDirty, saveToServer]);

  const getVisibleWidgets = useCallback(() => {
    return preferences.widgets.filter(w => w.visible).sort((a, b) => a.order - b.order);
  }, [preferences.widgets]);

  const setLayout = useCallback(
    (layout: 'grid' | 'list') => {
      updatePreferences(prev => ({ ...prev, layout }));
    },
    [updatePreferences]
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  return {
    preferences,
    updateWidgetVisibility,
    updateWidgetOrder,
    resetToDefault,
    getVisibleWidgets,
    setLayout,
    isLoading,
    isDirty,
    syncError,
    flushSave,
    reload: loadFromServer,
  };
};
